import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/contexts/AppContext';

interface AddCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddCategoryModal({ open, onOpenChange }: AddCategoryModalProps) {
  const { createCategory } = useApp();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [usp, setUsp] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      setLoading(true);
      const keywordsArray = keywords.trim() 
        ? keywords.split(',').map(k => k.trim()).filter(Boolean) 
        : [];
      await createCategory(
        name.trim(), 
        description.trim() || undefined,
        keywordsArray,
        usp.trim() || undefined,
        location.trim() || undefined
      );
      setLoading(false);
      setName('');
      setDescription('');
      setKeywords('');
      setUsp('');
      setLocation('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="wireframe-label">Category Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Post-Surgery Recovery"
              className="wireframe-input"
            />
          </div>
          
          <div>
            <Label className="wireframe-label">Description (optional)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this category..."
              className="wireframe-input min-h-[60px] resize-none"
            />
          </div>

          <div>
            <Label className="wireframe-label">Keywords (comma-separated)</Label>
            <Input
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="e.g., friendly staff, quick service, clean facility"
              className="wireframe-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Keywords to highlight in generated reviews
            </p>
          </div>

          <div>
            <Label className="wireframe-label">USP (Unique Selling Proposition)</Label>
            <Textarea
              value={usp}
              onChange={(e) => setUsp(e.target.value)}
              placeholder="e.g., 24/7 availability, award-winning team, latest technology"
              className="wireframe-input min-h-[60px] resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">
              What makes your service unique for this category
            </p>
          </div>

          <div>
            <Label className="wireframe-label">Location/Branch</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Downtown NYC, Main Street Branch"
              className="wireframe-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Specific location for hyper-local reviews
            </p>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading || !name.trim()}>
              {loading ? 'Saving...' : 'Save Category'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}